/* eslint-disable react/prop-types */
// This repo carries no prop-types dependency and no component declares them;
// disabling the rule here keeps the lint gate at zero new problems without
// adding a dependency for one file.
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, AlertCircle, Info } from 'lucide-react';
import {
  WINDOW_LABELS, SENTIMENT_ROWS, matrixRow, expectedLosses, fmtMusd, fmtUsd,
} from '@/lib/categoryIntel';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { fetchCategoryIntel, fetchCategoryIntelAccounts, fetchCategoryIntelSupplierMoves } from '@/lib/api/procurementServer';
import { expandCategoriesForApi } from '@/utils/edtechCategoryNames';
import { resolveDisplacementScope } from '@/lib/displacementTenantScopes';
import { useTranslation, translate } from '@/components/utils/translations';

// WINDOW_LABELS / SENTIMENT_ROWS (lib/categoryIntel) stay English data; these keys are their
// display labels, looked up by index / sentiment key at render time.
const WINDOW_LABEL_KEYS = ['procWin0_6Mo', 'procWin6_12Mo', 'procWin12_24Mo', 'procWin24PlusMo', 'procWinNoEndDate'];
const SENTIMENT_LABEL_KEYS = { neg: 'sentimentNegative', neu: 'sentimentNeutral', pos: 'sentimentPositive' };

const TONE = {
  hot: { text: 'text-[#D94C3D]', bg: '#D94C3D' },
  warn: { text: 'text-[#C8860D]', bg: '#C8860D' },
  good: { text: 'text-[#1E9E6A]', bg: '#1E9E6A' },
};

function shortName(category) {
  return String(category || '')
    .replace(/\s*\(Education only\)\s*/i, '')
    .replace(/Learning Management Systems \(LMS\)/i, 'LMS')
    .replace(/Student Information Systems \(SIS\)/i, 'SIS')
    .replace(/Library & Discovery Services.*/i, 'Library & Discovery')
    .replace(/Digital Course Content.*/i, 'Course Content')
    .replace(/Cybersecurity & Compliance Platforms/i, 'Cybersecurity')
    .replace(/Virtual Classroom & Video-Conferencing/i, 'Virtual Classroom')
    .replace(/IT Infrastructure \/ Storage/i, 'IT Infrastructure');
}

function Kpi({ label, value, unit, sub, tone, help }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span>{label}</span>
        {help ? (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger
                type="button"
                aria-label={t('procHowLabelIsMeasured', { label })}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2]"
              >
                <Info className="h-3 w-3" aria-hidden="true" />
              </TooltipTrigger>
              {/* max-w so the explanation wraps instead of becoming one long line */}
              <TooltipContent side="bottom" className="max-w-[320px] space-y-2 text-left normal-case tracking-normal">
                {help}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </div>
      <div className={`mt-1.5 text-3xl font-semibold tabular-nums leading-none ${tone || 'text-[#0A1A2F] dark:text-foreground'}`}>
        {value}{unit ? <span className="text-base font-semibold"> {unit}</span> : null}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}


/**
 * One snapshot of net position — winners or losers — ordered largest first.
 * Both groups share `max` so a bar means the same thing in either, which is the
 * point: the losing side is usually much longer than the winning side.
 */
// Hover pop-up on a net winner / loser row: the accounts it won and lost. Fetched on first hover and
// kept for the session; summarised to a few lines each way (Tiago, 2026-10-14).
const MOVES_CACHE = new Map();
const MOVES_SHOWN = 4;
function MovesList({ label, items }) {
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <div className="space-y-0.5">
      <div className="font-semibold">{label} {items.length}</div>
      {items.slice(0, MOVES_SHOWN).map((m, i) => (
        <div key={i} className="truncate">
          {m.account_name} <span className="opacity-70">{m.direction === 'won' ? t('procMoveFrom') : t('procMoveTo')} {m.other_supplier}{m.award_date ? ` · ${String(m.award_date).slice(0, 4)}` : ''}</span>
        </div>
      ))}
      {items.length > MOVES_SHOWN ? <div className="opacity-70">{t('procPlusNMore', { n: items.length - MOVES_SHOWN })}</div> : null}
    </div>
  );
}
function MovesPopup({ tenantId, category, country, name, children }) {
  const { t } = useTranslation();
  const key = JSON.stringify([category, country || '', name]);
  const [state, setState] = useState(() => MOVES_CACHE.get(key) || null);
  const load = () => {
    if (state || !tenantId) return;
    setState({ loading: true });
    fetchCategoryIntelSupplierMoves(tenantId, { category, supplier: name, country })
      .then((d) => { const s = { moves: d?.moves || [] }; MOVES_CACHE.set(key, s); setState(s); })
      .catch(() => setState({ error: true }));
  };
  const won = (state?.moves || []).filter((m) => m.direction === 'won');
  const lost = (state?.moves || []).filter((m) => m.direction === 'lost');
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip onOpenChange={(o) => { if (o) load(); }}>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="right" className="max-w-[300px] space-y-1.5 text-left text-xs normal-case tracking-normal">
          {!state || state.loading ? <div>{t('procLoading')}</div>
            : state.error ? <div>{t('procCouldNotLoadChanges')}</div>
              : (
                <>
                  <MovesList label={t('procWon')} items={won} />
                  <MovesList label={t('procLost')} items={lost} />
                  {!won.length && !lost.length ? <div>{t('procNoRecordedChanges')}</div> : null}
                </>
              )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function NetGroup({ title, rows, max, positive, tenantId, category, country }) {
  const { t } = useTranslation();
  if (!rows.length) return null;
  return (
    <div className="space-y-2.5">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      {rows.map(([name, gained, lost, net], i) => {
        const n = Number(net) || 0;
        return (
          <MovesPopup key={i} tenantId={tenantId} category={category} country={country} name={name}>
          <div tabIndex={0} className="grid cursor-default grid-cols-[1fr_88px_44px] items-center gap-3 rounded text-[13px] hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2]">
            <div className="min-w-0">
              <div className="truncate font-medium" title={name}>{name}</div>
              <div className="text-[11px] text-muted-foreground">{t('procWonLostCounts', { won: gained, lost })}</div>
            </div>
            <div className="h-[16px] rounded-sm bg-muted" aria-hidden="true">
              <div
                className="h-full rounded-sm"
                style={{ width: `${Math.max(3, (Math.abs(n) / max) * 100)}%`,
                         background: positive ? '#00C2C2' : '#D94C3D' }}
              />
            </div>
            <span className={`text-right font-semibold tabular-nums ${positive ? 'text-[#00A3A3]' : TONE.hot.text}`}>
              {positive ? `+${n}` : n}
            </span>
          </div>
          </MovesPopup>
        );
      })}
    </div>
  );
}

/**
 * Churn broken down by one dimension. Rows arrive as [label, rate, n] already
 * ordered highest rate first, capped at six by volume. `n` is shown because a
 * rate off 125 relationships deserves less weight than one off 3,158, and the
 * bars are scaled to the largest rate in view so differences stay legible.
 */
function BreakdownChart({ title, hint, rows, emptyNote }) {
  const { t } = useTranslation();
  const max = Math.max(...rows.map((r) => Number(r[1]) || 0), 1);
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="flex items-baseline justify-between border-b border-border px-5 py-3.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
      <div className="p-5">
        {rows.length === 0 ? (
          <p className="py-4 text-xs text-muted-foreground">{emptyNote}</p>
        ) : (
          <div className="space-y-3">
            {rows.map(([label, rate, n]) => (
              <div key={label} className="grid grid-cols-[132px_1fr_56px] items-center gap-3 text-[13px]">
                <span className="truncate font-medium text-muted-foreground" title={label}>{label}</span>
                <div
                  className="h-[22px] overflow-hidden rounded border border-border bg-muted"
                  title={t('procEstablishedRelationships', { n: Number(n).toLocaleString() })}
                >
                  <div
                    className="h-full rounded-l bg-[#00C2C2]"
                    style={{ width: `${Math.max(2, ((Number(rate) || 0) / max) * 100)}%` }}
                  />
                </div>
                <span className="text-right font-semibold tabular-nums">{Number(rate).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** One page per category. The switcher only appears when the tenant has more than one. */
function CategoryDisplacement({ categories = [], tenantId }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [cell, setCell] = useState(null);
  const [cellRows, setCellRows] = useState([]);
  const [cellLoading, setCellLoading] = useState(false);
  // Optional market scope. Null = global, which is the default: the page is a
  // worldwide read and the filter narrows it, not the other way round.
  const [country, setCountry] = useState(null);

  // A configured tenant gets its own ordered category list and its own market
  // scopes; the first category is the landing page. Anything unconfigured falls
  // back to whatever the Contract filter has selected.
  const scope = useMemo(() => resolveDisplacementScope(tenantId), [tenantId]);
  const effectiveCats = useMemo(
    () => (scope?.categories?.length ? scope.categories : (categories || [])),
    [scope, categories],
  );
  const COUNTRY_SCOPES = useMemo(
    // Default markets for every unconfigured tenant: UK + US (Tiago, 2026-10-27 — Ireland replaced by the US).
    () => (scope ? scope.countries : ['United Kingdom', 'United States']),
    [scope],
  );

  // Send BOTH the display name and the slug, as every other procurement call
  // does. The view keys on closed_deals.category ("IT Hardware (Education
  // only)") while this repo's official name is "IT Hardware"; the slug is what
  // carries the DB label across on the server side.
  const officialCats = useMemo(() => expandCategoriesForApi(effectiveCats), [effectiveCats]);
  const catKey = useMemo(() => officialCats.join('|'), [officialCats]);

  useEffect(() => {
    let cancelled = false;
    // The tenant gate runs before routing; calling without one is a guaranteed 403.
    if (!tenantId || !officialCats.length) { setLoading(false); return undefined; }
    setLoading(true);
    setError(null);
    fetchCategoryIntel(tenantId, officialCats, country)
      .then((d) => {
        if (cancelled) return;
        const raw = d?.categories || [];
        // Honour the configured order: the first entry is the default page.
        const order = effectiveCats.map((c) => String(c).toLowerCase());
        const list = order.length
          ? [...raw].sort((a, b) => {
              const ia = order.indexOf(String(a.category).toLowerCase());
              const ib = order.indexOf(String(b.category).toLowerCase());
              return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
            })
          : raw;
        setRows(list);
        setActiveCat((prev) => (list.some((r) => r.category === prev) ? prev : list[0]?.category ?? null));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || translate('procFailedToLoad'));
        setLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catKey, tenantId, country]);

  const row = useMemo(() => rows.find((r) => r.category === activeCat) || null, [rows, activeCat]);

  // switching category clears any open cell
  useEffect(() => { setCell(null); setCellRows([]); }, [activeCat]);

  useEffect(() => {
    if (!cell || !activeCat || !tenantId) return undefined;
    let cancelled = false;
    setCellLoading(true);
    fetchCategoryIntelAccounts(tenantId, {
      category: activeCat, sentiment: cell.key, window: cell.w,
    })
      .then((d) => { if (!cancelled) { setCellRows(d?.accounts || []); setCellLoading(false); } })
      .catch(() => { if (!cancelled) { setCellRows([]); setCellLoading(false); } });
    return () => { cancelled = true; };
  }, [cell, activeCat, tenantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('procLoadingCategoryIntel')}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-destructive">
        <AlertCircle className="w-4 h-4 mr-2" /> {error}
      </div>
    );
  }
  if (!row) {
    return (
      <div className="py-24 text-center text-sm text-muted-foreground">
        {t('procNoMeasuredHistory')}
      </div>
    );
  }

  const losses = expectedLosses(row);
  const pipeVals = [row.value_0_6_musd, row.value_6_12_musd, row.value_12_24_musd, row.value_24_musd]
    .map((v) => Number(v) || 0);
  const pipeMax = Math.max(...pipeVals, 1);
  const pipeColors = ['#D94C3D', '#C8860D', '#00C2C2', '#CFD7DE'];
  // Net position per supplier, not repeat pairs. In a fragmented market every
  // pair occurs exactly once (UK IT Hardware: 87 changes, none repeated), so the
  // pair panel rendered empty while the movement sat in the data. net_moves is
  // [name, gained, lost, net]; the view returns the top five each way.
  //
  // Split into two snapshots, each ordered by size. One scale across BOTH so the
  // asymmetry is visible: in IT Hardware the biggest loser is -32 against a
  // biggest winner of +13, and equal-length bars would hide that.
  const netMoves = Array.isArray(row.net_moves) ? row.net_moves : [];
  const netMax = Math.max(...netMoves.map((m) => Math.abs(Number(m[3]) || 0)), 1);
  const netWinners = netMoves.filter((m) => (Number(m[3]) || 0) > 0)
    .sort((a, b) => Number(b[3]) - Number(a[3]));
  const netLosers = netMoves.filter((m) => (Number(m[3]) || 0) < 0)
    .sort((a, b) => Number(a[3]) - Number(b[3]));

  return (
    <div className="space-y-4">
      {/* page header — the category IS the page */}
      <div className="flex flex-wrap items-start justify-between gap-5 border-b border-border pb-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-[#00A3A3]">{t('displacementRadar')}</div>
          <h2 className="mt-1.5 text-2xl font-semibold tracking-tight">{row.category}</h2>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            {t('procDisplacementSubtitle')}
          </p>
        </div>
        <div className="whitespace-nowrap text-right text-xs text-muted-foreground">
          <span className="mr-1.5 inline-block h-[7px] w-[7px] rounded-full bg-[#00C2C2] align-middle" />
          {t('procLastUpdatedDate', { date: String(row.computed_at || '').slice(0, 10) })}
        </div>
      </div>

      {/* a one-category tenant never sees this */}
      {rows.length > 1 && (
        <nav className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
          <span className="mr-1 border-r border-border pr-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t('procYourCategories')}
          </span>
          {rows.map((r) => (
            <button
              key={r.category}
              onClick={() => setActiveCat(r.category)}
              aria-current={r.category === activeCat ? 'page' : undefined}
              className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
                r.category === activeCat
                  ? 'bg-[#0A1A2F] font-semibold text-white dark:bg-[#00C2C2] dark:text-[#0A1A2F]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {shortName(r.category)}
            </button>
          ))}
        </nav>
      )}

      {/* market scope — hidden entirely for a global-only tenant */}
      {COUNTRY_SCOPES.length > 0 && (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('market')}</span>
        <button
          type="button"
          onClick={() => setCountry(null)}
          className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
            country === null ? 'bg-[#0A1A2F] font-semibold text-white dark:bg-[#00C2C2] dark:text-[#0A1A2F]'
                             : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}
        >
          {t('procGlobal')}
        </button>
        {COUNTRY_SCOPES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCountry(c)}
            className={`rounded-md px-3 py-1.5 text-[13px] transition-colors ${
              country === c ? 'bg-[#0A1A2F] font-semibold text-white dark:bg-[#00C2C2] dark:text-[#0A1A2F]'
                            : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'}`}
          >
            {c}
          </button>
        ))}
      </div>
      )}

      {/* AI read */}
      {row.narrative && (
        <div className="rounded-lg border border-[#00C2C2] bg-[#ECFFFE] p-5 dark:bg-[#0E2A2C]">
          <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#00A3A3]">
            <span>◇</span> {t('procHighlights')}
          </div>
          <p className="text-[15px] leading-relaxed text-foreground">{row.narrative}</p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label={t('procAnnualChurn')} value={`${row.churn_with_exits ?? row.churn_established ?? '—'}%`}
             sub={row.churn_with_exits != null
               ? t('procChurnSubSplit', {
                   switched: row.churn_established,
                   stopped: Math.round((Number(row.churn_with_exits) - Number(row.churn_established)) * 10) / 10,
                 })
               : t('procChurnSubPerYear')}
             tone={Number(row.churn_with_exits) >= 12 ? TONE.hot.text : Number(row.churn_with_exits) <= 8 ? TONE.good.text : undefined}
             help={<>
               <p>{t('procChurnHelpIntro')}</p>
               <p><strong>{t('procChurnHelpSwitchedLabel')}</strong> {t('procChurnHelpSwitchedBody')}</p>
               <p><strong>{t('procChurnHelpStoppedLabel')}</strong> {t('procChurnHelpStoppedBody')}</p>
               <p><strong>{t('procChurnHelpAnnualLabel')}</strong> {t('procChurnHelpAnnualBody')}</p>
               <p><strong>{t('procChurnHelpMultiLabel')}</strong> {t('procChurnHelpMultiBody')}</p>
             </>} />
        <Kpi label={t('procSuppliersPerAccount')} value={row.suppliers_per_account ?? '—'}
             sub={t('procPctBuyersUseOne', { n: row.pct_single_supplier ?? 0 })} />
        <Kpi label={t('procRebuyCycle')} value={row.cycle_months ?? '—'} unit={t('procUnitMo')} sub={t('procMedianGapBetweenAwards')} />
        <Kpi label={t('procReopeningWithin12Mo')} value={Number(row.w0_6 + row.w6_12).toLocaleString()}
             sub={t('procLiveContractsAcrossMarkets', { n: row.markets })} />
        <Kpi label={t('procValueInNext12Mo')} value={`$${Math.round((pipeVals[0] + pipeVals[1]) * 10) / 10}`} unit="m"
             sub={t('procFromNExpiringContracts', { n: Number(row.w0_6 + row.w6_12).toLocaleString() })} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* renewal pipeline */}
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-baseline justify-between border-b border-border px-5 py-3.5">
            <h3 className="text-sm font-semibold">{t('procRenewalPipeline')}</h3>
            <span className="text-xs text-muted-foreground">{t('procLiveContractsByTimeToExpiry')}</span>
          </div>
          <div className="space-y-3 p-5">
            {pipeVals.map((v, i) => (
              <div key={i} className="grid grid-cols-[72px_1fr_76px] items-center gap-3 text-[13px]">
                <span className="font-medium text-muted-foreground">{WINDOW_LABEL_KEYS[i] ? t(WINDOW_LABEL_KEYS[i]) : WINDOW_LABELS[i]}</span>
                <div className="h-[22px] overflow-hidden rounded border border-border bg-muted">
                  <div className="h-full rounded-l" style={{ width: `${Math.max(2, (v / pipeMax) * 100)}%`, background: pipeColors[i] }} />
                </div>
                <span className="text-right font-semibold tabular-nums">{fmtMusd(v)}</span>
              </div>
            ))}
            <p className="border-t border-border pt-3 text-xs text-muted-foreground">
              {t('procPipelineNoteBefore', { n: Number(row.w0_6).toLocaleString(), churn: row.churn_established })}{' '}
              <strong className="text-foreground">{losses.toLocaleString()}</strong>{' '}
              {t('procPipelineNoteAfter')}
            </p>
          </div>
        </div>

        {/* net position change per supplier */}
        <div className="rounded-lg border border-border bg-card">
          <div className="flex items-baseline justify-between border-b border-border px-5 py-3.5">
            <h3 className="text-sm font-semibold">{t('procNetPositionChange')}</h3>
            <span className="text-xs text-muted-foreground">{t('procAccountsWonMinusLost')}</span>
          </div>
          <div className="space-y-5 p-5">
            {netMoves.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t('procTooFewRepeatAwards')}
              </p>
            )}
            <NetGroup title={t('procNetWinners')} rows={netWinners} max={netMax} positive tenantId={tenantId} category={row.category} country={country} />
            <NetGroup title={t('procNetLosers')} rows={netLosers} max={netMax} positive={false} tenantId={tenantId} category={row.category} country={country} />
            {netMoves.length > 0 && (
              <p className="border-t border-border pt-3 text-xs text-muted-foreground">
                {t('procNetPositionNote')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* A country breakdown inside a single country says nothing, so when the
          page is scoped the institution chart takes the full width instead of
          sitting beside an empty panel. */}
      <div className={`grid gap-4 ${country ? '' : 'lg:grid-cols-2'}`}>
        {!country && (
          <BreakdownChart
            title={t('procChurnByCountry')}
            hint={t('procTopMarketsByVolume')}
            rows={Array.isArray(row.churn_by_country) ? row.churn_by_country : []}
            emptyNote={t('procChurnByCountryEmpty')}
          />
        )}
        <BreakdownChart
          title={t('procChurnByInstitutionType')}
          hint={t('procAnnualEstablishedRelationships')}
          rows={Array.isArray(row.churn_by_institution) ? row.churn_by_institution : []}
          emptyNote={t('procChurnByInstitutionEmpty')}
        />
      </div>

      {/* sentiment x decision window */}
      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-baseline justify-between border-b border-border px-5 py-3.5">
          <h3 className="text-sm font-semibold">{t('procIncumbentByDecisionWindow')}</h3>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-[120px_repeat(5,1fr)] gap-1.5">
            <div className="pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procIncumbent')}</div>
            {WINDOW_LABELS.map((w, wi) => (
              <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{WINDOW_LABEL_KEYS[wi] ? t(WINDOW_LABEL_KEYS[wi]) : w}</div>
            ))}
            {SENTIMENT_ROWS.map(({ key, label, tone }) => {
              const vals = matrixRow(row.sentiment_matrix, key);
              const rowMax = Math.max(...vals, 1);
              return (
                <React.Fragment key={key}>
                  <div className="flex items-center gap-2 pr-2 text-[13px] font-semibold">
                    <i className="h-2.5 w-2.5 flex-none rounded-sm" style={{ background: TONE[tone].bg }} />
                    {SENTIMENT_LABEL_KEYS[key] ? t(SENTIMENT_LABEL_KEYS[key]) : label}
                  </div>
                  {vals.map((n, w) => {
                    const priority = key === 'neg' && w < 2 && n > 0;
                    return (
                      <button
                        key={w}
                        type="button"
                        disabled={n === 0}
                        onClick={() => setCell({ key, w, label })}
                        className={`relative rounded-md border px-2 py-2.5 text-center transition-transform ${
                          n === 0
                            ? 'cursor-default border-border opacity-45'
                            : 'border-border hover:-translate-y-px hover:border-[#00C2C2]'
                        } ${cell && cell.key === key && cell.w === w ? 'ring-2 ring-[#0A1A2F] dark:ring-[#00C2C2]' : ''}`}
                        style={n === 0 ? undefined : {
                          background: `color-mix(in srgb, ${TONE[tone].bg} ${Math.round((0.14 + 0.5 * (n / rowMax)) * 100)}%, transparent)`,
                          boxShadow: priority ? 'inset 0 0 0 2px #D94C3D' : undefined,
                        }}
                      >
                        <span className="block text-lg font-semibold leading-tight tabular-nums">{Number(n).toLocaleString()}</span>
                        <span className="mt-0.5 block text-[11px] text-muted-foreground">{n === 1 ? t('procAccountLower') : t('procAccountsLower')}</span>
                      </button>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>

          {cell && (
            <div className="mt-5 border-t border-border pt-4">
              <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-[13px] font-semibold">
                  {t('procSentimentIncumbent', { sentiment: SENTIMENT_LABEL_KEYS[cell.key] ? t(SENTIMENT_LABEL_KEYS[cell.key]) : cell.label })} &middot;{' '}
                  {cell.w === 4 ? t('procNoContractEndDate') : t('procContractEndsIn', { window: WINDOW_LABEL_KEYS[cell.w] ? t(WINDOW_LABEL_KEYS[cell.w]) : WINDOW_LABELS[cell.w] })}
                </h4>
                {!cellLoading && cellRows.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {cellRows.length.toLocaleString()} {cellRows.length === 1 ? t('procAccountLower') : t('procAccountsLower')} &middot;{' '}
                    {t('procAmountTotal', { amount: fmtUsd(cellRows.reduce((a, r) => a + (Number(r.deal_value_usd) || 0), 0)) })}
                  </span>
                )}
              </div>

              {cellLoading ? (
                <div className="flex items-center py-6 text-xs text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('procLoadingAccounts')}
                </div>
              ) : cellRows.length === 0 ? (
                <p className="py-6 text-center text-xs text-muted-foreground">{t('procNoAccountsForCell')}</p>
              ) : (
                <div className="max-h-[420px] overflow-auto rounded border border-border">
                  <table className="w-full text-[13px]">
                    <thead className="sticky top-0 bg-muted">
                      <tr>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('account')}</th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('vendor')}</th>
                        <th className="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procContractEnd')}</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procContractValue')}</th>
                        <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t('procWhy')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cellRows.map((r, i) => (
                        <tr key={`${r.account_name}-${i}`} className="border-t border-border align-top">
                          <td className="px-3 py-2">
                            <span className="font-semibold">{r.account_name}</span>
                            {r.country && <span className="block text-[11px] text-muted-foreground">{r.country}</span>}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{r.supplier}</td>
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">{String(r.predicted_expiry || '').slice(0, 10)}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">{fmtUsd(r.deal_value_usd)}</td>
                          <td className="px-3 py-2 text-[12px] leading-relaxed text-muted-foreground">{r.rationale || '\u2014'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

    </div>
  );
}

export default React.memo(CategoryDisplacement);
